import React, { useState } from 'react';
import { Download, ExternalLink } from 'lucide-react';
import { ToolCardImage } from '../../../types/chat';
import { useImageJobImages } from '../../../hooks/useImageJobImages';
import { TaskyLightbox } from '../../tasky/modal';

interface ImageResultGridProps {
  jobId?: string | null;
  images: ToolCardImage[];
  /** バナー生成ならバナーページ、それ以外（組織バナー一覧等）はリンクなし */
  pageLink?: { href: string; label: string } | null;
}

/**
 * 完成画像の格子。job_id があれば表示時に presigned URL を取り直す（履歴対策）。クリックでライトボックス。
 */
const ImageResultGrid: React.FC<ImageResultGridProps> = ({ jobId, images, pageLink }) => {
  const { images: fetched, error } = useImageJobImages(jobId, Boolean(jobId), images);
  const list: ToolCardImage[] = jobId ? fetched : images;
  const [open, setOpen] = useState<string | null>(null);

  if (list.length === 0) {
    return error ? <div className="text-xs text-gray-400">画像の取得に失敗しました</div> : null;
  }

  return (
    <div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {list.map((img, index) => (
          <div
            key={`${img.id ?? index}-${index}`}
            className="group relative overflow-hidden rounded-lg border border-gray-200 transition-transform hover:-translate-y-0.5 hover:shadow-md"
          >
            {/* <a> を <button> の中に入れると DOM 入れ子違反になるため、兄弟にして重ねる */}
            <button type="button" className="block w-full" onClick={() => setOpen(img.url)} aria-label="画像を拡大">
              <img src={img.url} alt="生成画像" className="aspect-square w-full object-cover" loading="lazy" />
            </button>
            <a
              href={img.url}
              download
              target="_blank"
              rel="noopener noreferrer"
              aria-label="画像をダウンロード"
              className="absolute bottom-1 right-1 rounded-md bg-white/90 p-1 text-gray-600 opacity-0 shadow transition-opacity group-hover:opacity-100"
            >
              <Download className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          </div>
        ))}
      </div>
      {pageLink && (
        <a href={pageLink.href} className="mt-2 inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline">
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
          {pageLink.label}
        </a>
      )}
      <TaskyLightbox open={open !== null} onClose={() => setOpen(null)} src={open ?? undefined} alt="生成画像" />
    </div>
  );
};

export default ImageResultGrid;
